/**
 * Durée réelle de la vidéo d'une leçon (lot 4C) : `videoDuration`, posée par
 * le serveur depuis la vidéo liée. Un admin peut la récupérer à nouveau
 * (même route que le rattrapage en lot, limité à cette leçon).
 */

import React, { useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { Timer } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { Lecon } from '../../types';
import ChapitresLeconsApiService from '../../services/chapitresLeconsApi';
import type { BackfillDurationsResult } from '../../services/bookContentApi';
import { formatSecondsToMMSS } from '../school-books/BookExerciseForm';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { useRBAC } from '../../contexts/RBACContext';
import { useNotify } from '../../contexts/NotificationContext';
import { toUserMessage } from '../../lib/http';

/** Raison renvoyée par le serveur pour une leçon sans `videoId`. */
export const RAISON_SANS_VIDEO = 'pas de vidéo';

export const TEXTE_SANS_VIDEO = 'aucune vidéo liée';
export const TEXTE_DUREE_INCONNUE = 'inconnue (l\'app affiche la durée estimée)';

/** Libellé de la durée réelle : m:ss, « aucune vidéo liée » ou « inconnue ». */
export const libelleDuree = (lecon: Pick<Lecon, 'videoId' | 'videoDuration'>): string => {
  if (!lecon.videoId?.trim()) return TEXTE_SANS_VIDEO;
  const duree = lecon.videoDuration ?? 0;
  return duree > 0 ? formatSecondsToMMSS(duree) : TEXTE_DUREE_INCONNUE;
};

/** Message et gravité après une récupération, d'après le détail serveur. */
export const messageRecuperation = (
  res: BackfillDurationsResult
): { texte: string; gravite: 'success' | 'warning' } => {
  if (res.updated > 0) return { texte: 'Durée réelle mise à jour', gravite: 'success' };
  if (res.details[0]?.reason === RAISON_SANS_VIDEO) {
    return { texte: 'Aucune vidéo liée à cette leçon', gravite: 'warning' };
  }
  return { texte: 'Durée introuvable : la vidéo liée n\'a pas de durée mesurée', gravite: 'warning' };
};

interface LeconDureeVideoProps {
  lecon: Lecon;
}

const LeconDureeVideo: React.FC<LeconDureeVideoProps> = ({ lecon }) => {
  const { environment: env } = useEnvironment();
  const { isAdmin } = useRBAC();
  const { notify } = useNotify('content');
  const queryClient = useQueryClient();
  const [enCours, setEnCours] = useState(false);

  const recuperer = async () => {
    setEnCours(true);
    try {
      const res = await ChapitresLeconsApiService.backfillLeconDurations(env, [lecon.id], true);
      const { texte, gravite } = messageRecuperation(res);
      notify(texte, gravite);
      queryClient.invalidateQueries({ queryKey: ['lecon', lecon.id] });
    } catch (e: unknown) {
      notify(toUserMessage(e, 'Erreur lors de la récupération de la durée'), 'error');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Box display="flex" alignItems="center" gap={2} mt={1} flexWrap="wrap">
      <Typography variant="body2">Durée réelle : {libelleDuree(lecon)}</Typography>
      {isAdmin() && (
        <Button size="small" variant="outlined" onClick={recuperer} disabled={enCours}
          startIcon={enCours ? <CircularProgress size={16} /> : <Timer />}>
          Récupérer la durée réelle
        </Button>
      )}
    </Box>
  );
};

export default LeconDureeVideo;
